import { createQueryOptions, useTransport } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { previewBiddingIntradayOrderFormulaPrice } from '@ebalance/proto/bffuser/v1/bidding_intraday_service-BiddingIntradayService_connectquery'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useQueries } from '@tanstack/react-query'

import type { IntradayFeeTreatment } from '~/pages/IntradayBidding/hooks/useIntradayFeeTreatment'
import type { IntradayBulkFormulaRowPreview } from '~/pages/IntradayBidding/models/bulkCreate'
import { previewQueryStatus } from '~/pages/IntradayBidding/models/feeAutoConsider'
import type { IntradayFormulaInput } from '~/pages/IntradayBidding/models/intradayFormula'
import {
  resolveIntradayFormulaPricePreview,
  toFormulaPricePreviewRequest,
} from '~/pages/IntradayBidding/models/intradayFormulaPricePreview'

type Params = {
  targetDate: ProtoDate
  /** 問い合わせるコマ (作成対象の行だけ)。 空なら問い合わせない。 */
  timeCodes: readonly number[]
  areaCode: AreaCode | undefined
  direction: Side
  /** 入力が揃っていない間は undefined (問い合わせない)。 */
  formula: IntradayFormulaInput | undefined
  fee: IntradayFeeTreatment
}

/**
 * 一括作成のフォーミュラの、 作成対象のコマごとの計算結果 (AC-BIN-005c / AC-BIN-006b)。
 * 変数の値はコマごとに違うので、 札作成と同じプレビュー RPC をコマごとに引く (同じ入力の
 * 問い合わせは札作成と cache を共有する)。 request の組み立てと応答の読み方は札作成と共通
 * (`models/intradayFormulaPricePreview.ts`)。 計算は backend で、 frontend では計算しない。
 */
export function useIntradayBulkFormulaPreviews({
  targetDate,
  timeCodes,
  areaCode,
  direction,
  formula,
  fee,
}: Params): ReadonlyMap<number, IntradayBulkFormulaRowPreview> {
  const transport = useTransport()
  const targets =
    formula === undefined || areaCode === undefined
      ? []
      : timeCodes.map((timeCode) => ({
          targetDate,
          timeCode,
          areaCode,
          direction,
          formula,
        }))
  const results = useQueries({
    queries: targets.map((target) => ({
      ...createQueryOptions(
        previewBiddingIntradayOrderFormulaPrice,
        toFormulaPricePreviewRequest(target, fee.treatment),
        { transport },
      ),
      // 取得できない行は表の中で「—」 にする (全画面のエラーにしない)。
      throwOnError: false,
    })),
  })
  const previews = new Map(
    targets.map((target, i) => {
      const { data, isFetching, isError, failureCount } = results[i]
      return [
        target.timeCode,
        resolveIntradayFormulaPricePreview({
          status: previewQueryStatus({
            waiting: isFetching,
            isError,
            failureCount,
            hasData: data !== undefined,
          }),
          data,
          direction,
          treatment: fee.treatment,
        }),
      ]
    }),
  )
  for (const preview of previews.values()) fee.report(preview.fee)
  return previews
}
